import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';

import { DeviceInformation } from '@shared-ui/viewport-context';
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';
import { IntersectionTracker, useClickTracker } from '@shared-ui/clickstream-analytics-context';
import { useLocalization } from '@shared-ui/localization-context';

import { adaptShortList } from 'stores/adapters/bex-api/migration-adapters/common/shortlist-adapter';
import { getGalleryModel } from 'stores/adapters';
import { usePropertyListing } from 'stores/contexts';

import { PackageType } from 'models/multi-item/multi-item-types';
import { RfrrPageNamespace } from 'src/common/constants/analytics';

import { UitkButtonTheme } from 'uitk-react-button';
import { UitkFigure, UitkFigureAspectRatioType, UitkImage, UitkQueuedImage } from 'uitk-react-images';
import { UitkGalleryCarousel } from 'uitk-react-gallery-carousel';
import { UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';

import { hotelProductsSchema, imageGalleryAlgorithmTrackingSchema, multiItemProductsSchema } from 'utils/uis-prime-micro-messages';
import { updateSlotsParamForMapTravelAds } from 'utils/infosite-url-utils';

import { determineTarget, TargetableLink } from 'src/common/components/common/targetable-link';
import { DoubleLink } from 'src/common/components/common/media/double-link';
import { FavoriteButton } from 'components/common/shortlist/favorite-button';
import { PropertyFields } from '__generated__/api';

export interface ListingGalleryProps {
  deviceInformation?: DeviceInformation;
  listingClickHandler?: (property: PropertyFields.Fragment) => void;
  navigateOnClient?: (relativePath?: string) => void;
  mapListingView?: boolean;
  packageType?: PackageType;
  ratio?: UitkFigureAspectRatioType;
}

export interface ListingGalleryState {
  currentGalleryIndex: number;
}

export const ListingGallery: React.FC<ListingGalleryProps> = props => {
  const l10n = useLocalization();
  const property = usePropertyListing();
  const track = useClickTracker();
  const [currentGalleryIndex, setCurrentGalleryIndex] = React.useState(0);

  const { deviceInformation, listingClickHandler, mapListingView, navigateOnClient, packageType, ratio } = props;

  const { id, name, propertyDetailsLink, imageGallery, propertyImage, shortList, sponsoredListing } = property;
  const images = imageGallery?.images;

  const hotelId = parseInt(id, 10);
  const sponsoredSuffix = sponsoredListing ? '-sponsored' : '';

  /*
   * To be followed up soon on surfacing analytics data from Bex-Api
   * https://jira.expedia.biz/browse/LSHOP-256
   */
  const referrerId = `MainCarousel.property-listing-image-carousel${sponsoredSuffix}.GalleryCarousel`;
  const uisPrimeMicroMessages = galleryIndex => [
    imageGalleryAlgorithmTrackingSchema(
      hotelId,
      parseInt((images && images[galleryIndex]?.imageId) || '', 10),
      'heroaesthetics.v20190411-hsc.v20190223'
    ),
  ];

  const commonClickFns = () => {
    if (listingClickHandler) {
      listingClickHandler(property);
    }

    const trackMethod = packageType ? multiItemProductsSchema : hotelProductsSchema;
    const imagesSubjectId = images ? images[currentGalleryIndex]?.subjectId : '';

    if (propertyDetailsLink?.clientSideAnalytics) {
      track(propertyDetailsLink.clientSideAnalytics.referrerId, propertyDetailsLink.clientSideAnalytics.linkName, true, [
        ...trackMethod(property, packageType),
      ]);
    }

    track(
      `HOT.SR:PHOTOS.${hotelId}.${imagesSubjectId}`,
      'Image clicked on HSR image gallery',
      true,
      uisPrimeMicroMessages(currentGalleryIndex)
    );
  };

  const getLinkDestination = () => {
    let to = sponsoredListing && packageType ? propertyDetailsLink?.uri.value : propertyDetailsLink?.uri.relativePath;
    if (sponsoredListing && mapListingView) {
      to = updateSlotsParamForMapTravelAds(to || '');
    }

    return to;
  };

  const navigateToDestination = () => {
    commonClickFns();

    const target = determineTarget(deviceInformation, packageType !== null);

    if (target) {
      window.open(getLinkDestination(), target);
    } else {
      navigateOnClient?.(getLinkDestination());
    }
  };

  if (!imageGallery || images?.length === 0) {
    if (propertyImage) {
      const doubleLinkProps = {
        to: getLinkDestination(),
        onClick: commonClickFns,
      };

      return (
        <TargetableLink>
          <DoubleLink {...doubleLinkProps}>
            <UitkFigure imageFit="cover">
              <UitkImage alt={propertyImage.image.description} placeholderImage={false} src={propertyImage.image.url} lazyLoading="lazy" />
            </UitkFigure>
          </DoubleLink>
        </TargetableLink>
      );
    }

    return (
      <UitkFigure imageFit="cover">
        <UitkImage alt="" placeholderImage={true} />
      </UitkFigure>
    );
  }

  const updateIndex = (index: number) => {
    if (index >= 0) {
      const clickDirection = currentGalleryIndex - index === -1 || currentGalleryIndex - index > 1 ? 'clickRight' : 'clickLeft';
      track(
        `${RfrrPageNamespace.Search}.${referrerId}.${clickDirection}.${index}`,
        'Change image in listing carousel',
        undefined,
        uisPrimeMicroMessages(index)
      );

      setCurrentGalleryIndex(index);
    }
  };

  return (
    <ErrorBoundary
      label="hotel.information.details.photoGallery"
      fallback={<UitkQueuedImage src={images && images[0].image.url} useBackgroundImage={true} />}
    >
      <UitkLayoutFlexItem grow={1}>
        <IntersectionTracker
          tag="span"
          referrerId={`${RfrrPageNamespace.Search}.view.impression.${referrerId}`}
          linkName="Listing carousel view impression."
          additionalUisPrimeMicroMessages={uisPrimeMicroMessages(currentGalleryIndex)}
        >
          <UitkGalleryCarousel
            allyHeadingLevel={4}
            allyHeadingText={imageGallery.accessibilityLabel || ''}
            carouselId={`property-listing-${hotelId}${sponsoredSuffix}`}
            carouselName={`property-listing-${hotelId}-image-carousel${sponsoredSuffix}`}
            currentIndex={currentGalleryIndex}
            enablePreload
            images={getGalleryModel(imageGallery).images}
            navTheme={UitkButtonTheme.TRANSPARENT}
            onImageClick={navigateToDestination}
            onIndexChange={updateIndex}
            ratio={ratio}
            showGalleryButton={false}
            showScrim
            placeholderImage
          />
        </IntersectionTracker>
      </UitkLayoutFlexItem>
      <Experiment name="trips_toggle_BEXG_trip_planning_release_1">
        <ExperimentControl>
          {shortList?.metaData.hotelId && (
            <UitkLayoutPosition type="absolute" position={{ top: 'zero', right: 'zero', zindex: 'layer3' }}>
              <FavoriteButton
                label={l10n.formatText('hotels.searchResults.shortlist.saveToList.a11yLabel', [name])}
                onSaveMessage={l10n.formatText('hotels.shortlist.saveToList.notification', [name])}
                shortlistModel={adaptShortList(shortList) || undefined}
              />
            </UitkLayoutPosition>
          )}
        </ExperimentControl>
        <ExperimentVariant bucket={1} />
      </Experiment>
    </ErrorBoundary>
  );
};
